import { scopedSetter } from "../api/scopedSetter.js";
import { create } from "zustand";
import { t } from "../i18n/index.js";
import { toast } from "../ui/Toast.js";
import { errorText } from "../base/state.js";
import { cosmeticsApi, luckyApi, passesApi, shopsApi, storeApi, vipApi } from "./api.js";
import type {
  Bundle,
  CosmeticView,
  LuckyView,
  PurchaseResult,
  ShopView,
  StoreCard,
  StoreView,
  VipView,
} from "./types.js";
import { useSession } from "../state/session.js";
import { serverNow } from "../state/serverTime.js";
import { CatalogueCache } from "./catalogueCache.js";
import { catalogueCards, catalogueExpiresAt } from "./catalogue.js";
import { preloadCardArt } from "./art.js";

const catalogueCache = new CatalogueCache(
  async () => {
    const view = await storeApi.catalogue();
    await preloadCardArt(catalogueCards(view).map((card) => card.art));
    return view;
  },
  catalogueExpiresAt,
  serverNow,
);
let catalogueRequest = 0;
export function resetCatalogueCache(): void { catalogueRequest++; catalogueCache.reset(); }

export type StoreTab =
  | "featured"
  | "packs"
  | "daily"
  | "passes"
  | "speedups"
  | "resources"
  | "fabrication"
  | "vip"
  | "lucky"
  | "cosmetics"
  | "shops";
export const STORE_TABS: StoreTab[] = [
  "featured",
  "packs",
  "daily",
  "passes",
  "speedups",
  "resources",
  "fabrication",
  "vip",
  "lucky",
  "cosmetics",
  "shops",
];

/** The purchase celebration: what was granted and where it came from (crate slam, fly-in). */
export interface PurchaseFx {
  key: string;
  granted: Bundle;
  doubled: boolean;
  vipPoints: number;
  nextStep: StoreCard | null;
  at: number;
}

interface StoreState {
  view: StoreView | null;
  loading: boolean;
  purchasing: string | null;
  error: string | null;
  confirm: StoreCard | null;
  fx: PurchaseFx | null;
  vip: VipView | null;
  lucky: LuckyView | null;
  shops: ShopView[] | null;
  cosmetics: CosmeticView[] | null;
  /** Flash sales announced by push since the last catalogue load (HUD badge). */
  flashCount: number;
  offerCount: number;
  refresh(force?: boolean): Promise<void>;
  invalidate(): void;
  refreshVip(): Promise<void>;
  refreshLucky(): Promise<void>;
  refreshShops(): Promise<void>;
  refreshCosmetics(): Promise<void>;
  openConfirm(card: StoreCard | null): void;
  buy(key: string, opts?: { premiumPlus?: boolean; unitRowId?: string }): Promise<PurchaseResult | null>;
  celebrate(fx: Omit<PurchaseFx, "at">): void;
  clearFx(): void;
  noteFlash(): void;
  noteOffer(): void;
  seen(): void;
  run<T>(op: () => Promise<T>, okText?: string): Promise<T | null>;
}

export const useStore = create<StoreState>((set, get) => ({
  view: null,
  loading: false,
  purchasing: null,
  error: null,
  confirm: null,
  fx: null,
  vip: null,
  lucky: null,
  shops: null,
  cosmetics: null,
  flashCount: 0,
  offerCount: 0,
  invalidate: () => {
    catalogueCache.invalidate();
  },
  refresh: async (force = false) => {
    const set = scopedSetter<StoreState>(useStore.setState);
    const accountId = useSession.getState().account?.id;
    if (!accountId) return;
    if (force) catalogueCache.invalidate();
    const request = ++catalogueRequest;
    set({ loading: true, error: null });
    try {
      const view = await catalogueCache.load(accountId);
      if (request === catalogueRequest && accountId === useSession.getState().account?.id)
        set({ ...(view ? { view } : {}), loading: false });
    } catch (e) {
      if (request === catalogueRequest && accountId === useSession.getState().account?.id) {
        set({ loading: false, error: errorText(e) });
        if (set.current()) toast.error(errorText(e));
      }
    }
  },
  refreshVip: async () => {
    const set = scopedSetter<StoreState>(useStore.setState);
    const owner = useSession.getState().account?.id;
    if (!owner) return;
    try {
      const vip = await vipApi.view();
      if (owner === useSession.getState().account?.id) set({ vip });
    } catch (e) {
      if (owner === useSession.getState().account?.id) if (set.current()) toast.error(errorText(e));
    }
  },
  refreshLucky: async () => {
    const set = scopedSetter<StoreState>(useStore.setState);
    const owner = useSession.getState().account?.id;
    if (!owner) return;
    try {
      const lucky = await luckyApi.view();
      if (owner === useSession.getState().account?.id) set({ lucky });
    } catch (e) {
      if (owner === useSession.getState().account?.id) if (set.current()) toast.error(errorText(e));
    }
  },
  refreshShops: async () => {
    const set = scopedSetter<StoreState>(useStore.setState);
    const owner = useSession.getState().account?.id;
    if (!owner) return;
    try {
      const shops = await shopsApi.list();
      if (owner === useSession.getState().account?.id) set({ shops });
    } catch (e) {
      if (owner === useSession.getState().account?.id) if (set.current()) toast.error(errorText(e));
    }
  },
  refreshCosmetics: async () => {
    const set = scopedSetter<StoreState>(useStore.setState);
    const owner = useSession.getState().account?.id;
    if (!owner) return;
    try {
      const cosmetics = await cosmeticsApi.list();
      if (owner === useSession.getState().account?.id) set({ cosmetics });
    } catch (e) {
      if (owner === useSession.getState().account?.id) if (set.current()) toast.error(errorText(e));
    }
  },
  openConfirm: (card) => set({ confirm: card }),
  buy: async (key, opts = {}) => {
    const set = scopedSetter<StoreState>(useStore.setState);
    const owner = useSession.getState().account?.id;
    if (!owner || get().purchasing) return null;
    set({ purchasing: key });
    try {
      const res = await get().run(() => storeApi.buy(key, opts));
      if (!res || !set.current()) return null;
      set({ confirm: null });
      get().celebrate({
        key: res.key,
        granted: res.granted,
        doubled: res.doubled,
        vipPoints: res.vipPoints,
        nextStep: res.nextStep,
      });
      if (res.vipPoints > 0) if (set.current()) toast.reward(t("store.toast.vip", { n: res.vipPoints }));
      void get().refresh(true);
      if (get().vip) void get().refreshVip();
      if (res.granted.cosmetics?.length) void get().refreshCosmetics();
      return res;
    } finally {
      if (owner === useSession.getState().account?.id) set({ purchasing: null });
    }
  },
  celebrate: (fx) => set({ fx: { ...fx, at: Date.now() } }),
  clearFx: () => set({ fx: null }),
  noteFlash: () => set((s) => ({ flashCount: s.flashCount + 1 })),
  noteOffer: () => set((s) => ({ offerCount: s.offerCount + 1 })),
  seen: () => set({ flashCount: 0, offerCount: 0 }),
  run: async (op, okText) => {
    const set = scopedSetter<StoreState>(useStore.setState);
    const owner = useSession.getState().account?.id;
    if (!owner) return null;
    try {
      const res = await op();
      if (owner !== useSession.getState().account?.id || !set.current()) return null;
      if (okText) toast.success(okText);
      return res;
    } catch (e) {
      if (owner === useSession.getState().account?.id) if (set.current()) toast.error(errorText(e));
      return null;
    }
  },
}));

// Session lifetime owns synchronous resets; no top-level reverse subscription during bootstrap imports.

export const passes = passesApi;
